import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Typography,
  Alert,
  Stack,
  Tooltip,
  alpha,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Payment as PaymentIcon,
  CreditCard as CreditCardIcon,
  PictureAsPdf as PdfIcon,
  TableChart as ExcelIcon,
  Visibility as ViewIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  cardHolderAPI,
  CardHolder,
  CreateCardHolderData,
  UpdateCardHolderData,
  CardHolderSortField,
  CardHolderSortOrder,
} from "../../services/cardHolderAPI";
import { useAuthStore } from "../../stores/authStore";
import { useRBAC } from "../../hooks/useRBAC";
import PageHeader from "../../components/layout/PageHeader";
import DataListCard from "../../components/layout/DataListCard";
import HolderAvatar from "../../components/layout/HolderAvatar";
import TableActionButton from "../../components/layout/TableActionButton";
import CardHolderFormFields, {
  CardHolderFormValues,
} from "../../components/cardHolders/CardHolderFormFields";
import { maskAadhar, normalizeAadhar } from "../../utils/aadhar";
import {
  exportCardHoldersExcel,
  exportCardHoldersPdf,
  fetchAllCardHolders,
} from "../../utils/cardHolderExport";
import { tokens } from "../../theme";

const cardHolderSchema = yup.object({
  firstName: yup.string().required("First name is required"),
  lastName: yup.string().required("Last name is required"),
  email: yup.string().email("Invalid email format").optional(),
  phone: yup.string().required("Phone is required"),
  aadharNumber: yup
    .string()
    .required("Aadhar number is required")
    .test("aadhar", "Aadhar must be 12 digits", (v) => normalizeAadhar(v || "").length === 12),
  city: yup.string().required("City is required"),
  block: yup.string().required("Block is required"),
  district: yup.string().required("District is required"),
  state: yup.string().required("State is required"),
  address: yup.string().required("Complete address is required"),
  status: yup
    .string()
    .oneOf(["active", "inactive"])
    .required("Status is required"),
});

type StatusFilter = "all" | "active" | "inactive";
type UiSortOrder = "asc" | "desc";

/** Compact header actions — aligned with export icon buttons */
const headerActionBtnSx = {
  minHeight: 32,
  py: 0.375,
  px: 1.25,
  fontSize: "0.8125rem",
  lineHeight: 1.2,
  "& .MuiButton-startIcon": { mr: 0.5 },
  "& .MuiButton-startIcon > *:nth-of-type(1)": { fontSize: 18 },
} as const;

const headerExportIconBtnSx = {
  width: 32,
  height: 32,
  p: 0.5,
  border: "1px solid",
  borderColor: "divider",
} as const;

const CardHoldersPage: React.FC = () => {
  const { canPerformRecharge, hasAnyRole, canCreate } = useRBAC();
  const isSuperAdmin = hasAnyRole("SUPER_ADMIN");
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [sortBy, setSortBy] = useState<CardHolderSortField>("holderName");
  const [sortOrder, setSortOrder] = useState<UiSortOrder>("asc");

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 400);
    return () => clearTimeout(timer);
  }, [search]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingCardHolder, setEditingCardHolder] = useState<CardHolder | null>(null);
  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean;
    cardHolderId: string | null;
  }>({ open: false, cardHolderId: null });
  const [alert, setAlert] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);
  const [exporting, setExporting] = useState<"pdf" | "excel" | null>(null);

  const buildExportFilters = () => ({
    sortBy,
    sortOrder: sortOrder.toUpperCase() as CardHolderSortOrder,
    ...(debouncedSearch ? { search: debouncedSearch } : {}),
    ...(statusFilter === "active" ? { isActive: true } : {}),
    ...(statusFilter === "inactive" ? { isActive: false } : {}),
  });

  const handleExportPdf = async () => {
    setExporting("pdf");
    setAlert(null);
    try {
      const holders = await fetchAllCardHolders(buildExportFilters());
      if (!holders.length) {
        setAlert({ type: "error", message: "No card holders to export." });
        return;
      }
      exportCardHoldersPdf(holders);
      setAlert({
        type: "success",
        message: `Exported ${holders.length} card holder(s) as PDF.`,
      });
    } catch (err: any) {
      setAlert({
        type: "error",
        message:
          err.response?.data?.message || err.message || "PDF export failed",
      });
    } finally {
      setExporting(null);
    }
  };

  const handleExportExcel = async () => {
    setExporting("excel");
    setAlert(null);
    try {
      const holders = await fetchAllCardHolders(buildExportFilters());
      if (!holders.length) {
        setAlert({ type: "error", message: "No card holders to export." });
        return;
      }
      exportCardHoldersExcel(holders);
      setAlert({
        type: "success",
        message: `Exported ${holders.length} card holder(s) as Excel (CSV).`,
      });
    } catch (err: any) {
      setAlert({
        type: "error",
        message:
          err.response?.data?.message || err.message || "Excel export failed",
      });
    } finally {
      setExporting(null);
    }
  };

  const { data, isLoading, error } = useQuery({
    queryKey: [
      "cardHolders",
      page,
      rowsPerPage,
      debouncedSearch,
      statusFilter,
      sortBy,
      sortOrder,
    ],
    queryFn: () =>
      cardHolderAPI.getCardHolders({
        pageNo: page + 1,
        pageSize: rowsPerPage,
        sortBy,
        sortOrder: sortOrder.toUpperCase() as CardHolderSortOrder,
        ...(debouncedSearch ? { search: debouncedSearch } : {}),
        ...(statusFilter === "active" ? { isActive: true } : {}),
        ...(statusFilter === "inactive" ? { isActive: false } : {}),
      }),
  });

  const { mutate: createCardHolder } = useMutation({
    mutationFn: (payload: CreateCardHolderData) =>
      cardHolderAPI.createCardHolder(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cardHolders"] });
      setAlert({ type: "success", message: "Card holder created successfully!" });
      handleCloseDialog();
    },
    onError: (err: any) => {
      setAlert({
        type: "error",
        message: err.response?.data?.message || "Failed to create card holder",
      });
    },
  });

  const { mutate: updateCardHolder } = useMutation({
    mutationFn: ({ id, data: payload }: { id: string; data: UpdateCardHolderData }) =>
      cardHolderAPI.updateCardHolder(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cardHolders"] });
      setAlert({ type: "success", message: "Card holder updated successfully!" });
      handleCloseDialog();
    },
    onError: (err: any) => {
      setAlert({
        type: "error",
        message: err.response?.data?.message || "Failed to update card holder",
      });
    },
  });

  const { mutate: deleteCardHolder } = useMutation({
    mutationFn: (id: string) => cardHolderAPI.deleteCardHolder(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cardHolders"] });
      setAlert({ type: "success", message: "Card holder deactivated!" });
      setDeleteDialog({ open: false, cardHolderId: null });
    },
    onError: (err: any) => {
      setAlert({
        type: "error",
        message: err.response?.data?.message || "Failed to delete card holder",
      });
    },
  });

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<CardHolderFormValues>({
    resolver: yupResolver(cardHolderSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      aadharNumber: "",
      city: "",
      block: "",
      district: "",
      state: "",
      address: "",
      status: "active",
    },
  });

  const handleOpenDialog = (cardHolder?: CardHolder) => {
    if (cardHolder) {
      setEditingCardHolder(cardHolder);
      reset({
        firstName: cardHolder.holderName.split(" ")[0] || "",
        lastName: cardHolder.holderName.split(" ").slice(1).join(" ") || "",
        email: cardHolder.email || "",
        phone: cardHolder.phone,
        aadharNumber:
          cardHolder.aadharNumber && !String(cardHolder.aadharNumber).includes("X")
            ? cardHolder.aadharNumber
            : "",
        city: cardHolder.city || "",
        block: cardHolder.block || "",
        district: cardHolder.district || "",
        state: cardHolder.state || "",
        address: cardHolder.address || "",
        status: cardHolder.isActive ? "active" : "inactive",
      });
    } else {
      if (!canCreate("cardholders")) {
        setAlert({
          type: "error",
          message: "You do not have permission to create card holders",
        });
        return;
      }
      setEditingCardHolder(null);
      reset({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        aadharNumber: "",
        city: "",
        block: "",
        district: "",
        state: "",
        address: "",
        status: "active",
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingCardHolder(null);
    reset();
  };

  const handleSort = (field: CardHolderSortField) => {
    if (sortBy === field) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(field);
      setSortOrder("asc");
    }
    setPage(0);
  };

  const sortableHeader = (label: string, field: CardHolderSortField, align?: "right") => (
    <TableCell align={align} sortDirection={sortBy === field ? sortOrder : false}>
      <TableSortLabel
        active={sortBy === field}
        direction={sortBy === field ? sortOrder : "asc"}
        onClick={() => handleSort(field)}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );

  const statusChip = (active: boolean) => (
    <Chip
      label={active ? "Active" : "Inactive"}
      size="small"
      sx={{
        fontWeight: 600,
        fontSize: "0.75rem",
        bgcolor: active ? tokens.colors.success.light : tokens.colors.error.light,
        color: active ? tokens.colors.success.main : tokens.colors.error.main,
        border: "none",
      }}
    />
  );

  const filterChips = (
    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap alignItems="center">
      <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
        Status
      </Typography>
      {(["all", "active", "inactive"] as StatusFilter[]).map((s) => (
        <Chip
          key={s}
          label={s === "all" ? "All" : s === "active" ? "Active" : "Inactive"}
          size="small"
          onClick={() => {
            setStatusFilter(s);
            setPage(0);
          }}
          sx={{
            fontWeight: 600,
            cursor: "pointer",
            bgcolor:
              statusFilter === s
                ? alpha(tokens.colors.primary.main, 0.15)
                : "background.paper",
            color: statusFilter === s ? "primary.dark" : "text.secondary",
            border: "1px solid",
            borderColor: statusFilter === s ? "primary.main" : "divider",
          }}
        />
      ))}
    </Stack>
  );

  return (
    <Box>
      <PageHeader
        title="Card Holders"
        subtitle="Manage your customers who own NFC cards"
        breadcrumbs={[
          { label: "Dashboard", to: "/dashboard" },
          { label: "Card Holders" },
        ]}
        action={
          <>
            {isSuperAdmin && (
              <>
                <Tooltip title="Export PDF (all matching filters)">
                  <span>
                    <IconButton
                      size="small"
                      sx={headerExportIconBtnSx}
                      onClick={handleExportPdf}
                      disabled={Boolean(exporting)}
                    >
                      <PdfIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Export Excel — CSV for Excel (all matching filters)">
                  <span>
                    <IconButton
                      size="small"
                      sx={headerExportIconBtnSx}
                      onClick={handleExportExcel}
                      disabled={Boolean(exporting)}
                    >
                      <ExcelIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </span>
                </Tooltip>
              </>
            )}
            <Button
              size="small"
              variant="outlined"
              startIcon={<CreditCardIcon />}
              onClick={() => navigate("/nfc-cards")}
              sx={{ ...headerActionBtnSx, borderColor: "divider", color: "text.primary" }}
            >
              NFC Cards
            </Button>
            {canPerformRecharge() && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<PaymentIcon />}
                onClick={() => navigate("/quick-recharge")}
                sx={{ ...headerActionBtnSx, borderColor: "divider", color: "text.primary" }}
              >
                Quick Recharge
              </Button>
            )}
            {canCreate("cardholders") && (
              <Button
                size="small"
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => navigate("/cardholders/new")}
                sx={headerActionBtnSx}
              >
                Add Holder
              </Button>
            )}
          </>
        }
      />

      {alert && (
        <Alert severity={alert.type} sx={{ mb: 2 }} onClose={() => setAlert(null)}>
          {alert.message}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Error loading card holders: {error.message || "Please try again later"}
        </Alert>
      )}

      <DataListCard
        searchPlaceholder="Search name, phone, Aadhar, city…"
        searchValue={search}
        onSearchChange={(v) => {
          setSearch(v);
          setPage(0);
        }}
        filters={filterChips}
        footer={
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            count={data?.total || 0}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            sx={{ width: "100%" }}
          />
        }
      >
        {isLoading ? (
          <Box display="flex" justifyContent="center" py={6}>
            <Typography color="text.secondary">Loading card holders…</Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small" sx={{ width: "100%" }}>
              <TableHead>
                <TableRow>
                  <TableCell width={52}>S.No.</TableCell>
                  {sortableHeader("Customer", "holderName")}
                  {sortableHeader("Aadhar", "aadharNumber")}
                  {sortableHeader("Phone", "phone")}
                  {sortableHeader("City", "city")}
                  {sortableHeader("Branch", "branch")}
                  {sortableHeader("Status", "status")}
                  <TableCell align="right">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data?.items.map((cardHolder: CardHolder, index) => (
                  <TableRow
                    key={cardHolder.id}
                    hover
                    sx={{ cursor: "pointer" }}
                    onClick={() => navigate(`/cardholders/${cardHolder.id}`)}
                  >
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {page * rowsPerPage + index + 1}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={1.5}>
                        <HolderAvatar name={cardHolder.holderName} size={38} />
                        <Box minWidth={0}>
                          <Typography variant="body2" fontWeight={600} noWrap>
                            {cardHolder.holderName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Card holder
                          </Typography>
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace" color="text.secondary">
                        {maskAadhar(cardHolder.aadharNumber)}
                      </Typography>
                    </TableCell>
                    <TableCell>{cardHolder.phone}</TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap>
                        {cardHolder.city || "—"}
                      </Typography>
                    </TableCell>
                    <TableCell>{cardHolder.branch?.name || "—"}</TableCell>
                    <TableCell>{statusChip(cardHolder.isActive)}</TableCell>
                    <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <TableActionButton
                          title="View profile"
                          onClick={() => navigate(`/cardholders/${cardHolder.id}`)}
                        >
                          <ViewIcon sx={{ fontSize: 18 }} />
                        </TableActionButton>
                        <TableActionButton
                          title="NFC cards"
                          variant="primary"
                          onClick={() => navigate(`/nfc-cards?holderId=${cardHolder.id}`)}
                        >
                          <CreditCardIcon sx={{ fontSize: 18 }} />
                        </TableActionButton>
                        <TableActionButton
                          title="Edit"
                          variant="primary"
                          onClick={() => handleOpenDialog(cardHolder)}
                        >
                          <EditIcon sx={{ fontSize: 18 }} />
                        </TableActionButton>
                        <TableActionButton
                          title="Deactivate"
                          variant="danger"
                          onClick={() =>
                            setDeleteDialog({
                              open: true,
                              cardHolderId: cardHolder.id,
                            })
                          }
                        >
                          <DeleteIcon sx={{ fontSize: 18 }} />
                        </TableActionButton>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
                {(!data?.items || data.items.length === 0) && !isLoading && (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                      <Typography color="text.secondary">No card holders found</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DataListCard>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <form
          onSubmit={handleSubmit((formData) => {
            const aadhar = normalizeAadhar(formData.aadharNumber);
            const payload = {
              holderName: `${formData.firstName} ${formData.lastName}`.trim(),
              email: formData.email || "",
              phone: formData.phone,
              ...(aadhar.length === 12 ? { aadharNumber: aadhar } : {}),
              city: formData.city.trim(),
              block: formData.block.trim(),
              district: formData.district.trim(),
              state: formData.state.trim(),
              address: formData.address.trim(),
              ...(user?.branchId ? { branchId: user.branchId } : {}),
            };

            if (editingCardHolder) {
              updateCardHolder({
                id: editingCardHolder.id,
                data: {
                  ...payload,
                  isActive: formData.status === "active",
                },
              });
            } else {
              if (aadhar.length !== 12) return;
              createCardHolder({ ...payload, aadharNumber: aadhar });
            }
          })}
        >
          <DialogTitle sx={{ fontWeight: 700 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="h6" fontWeight={700}>
                {editingCardHolder ? "Edit Card Holder" : "Add Card Holder"}
              </Typography>
              <IconButton size="small" onClick={handleCloseDialog}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Stack>
          </DialogTitle>
          <DialogContent dividers sx={{ maxHeight: "72vh" }}>
            <Box sx={{ mt: 0.25 }}>
              <CardHolderFormFields
                control={control}
                errors={errors}
                watch={watch}
                showStatus={!!editingCardHolder}
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={handleCloseDialog} color="inherit">
              Cancel
            </Button>
            <Button type="submit" variant="contained">
              {editingCardHolder ? "Save Changes" : "Add Holder"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, cardHolderId: null })}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Deactivate holder</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to deactivate this card holder?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, cardHolderId: null })}>
            Cancel
          </Button>
          <Button
            onClick={() =>
              deleteDialog.cardHolderId && deleteCardHolder(deleteDialog.cardHolderId)
            }
            color="error"
            variant="contained"
          >
            Yes, Deactivate
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CardHoldersPage;
